import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CourseDetailEntity, CourseRouteEntity, CourseStopEntity, PlaceRef, TimetableRef } from 'src/entities/Course.entity';
import { releaseCourseSave, tryAcquireCourseSave } from 'src/hooks/courseSaveGate';
import { useEndStopMutation, useSaveRouteMutation } from 'src/hooks/useCourseMutations';
import { parseCourseMasterError } from 'src/utils/courseMasterError';

import AddStopDialog from './AddStopDialog';
import { findTimeOrderError, todayString, toStopPayload } from './courseMasterUtils';
import EndStopDialog from './EndStopDialog';
import LoadingPlaceRow from './LoadingPlaceRow';
import StopRow, { STOP_COLS } from './StopRow';

const muted = 'text.secondary';
type Props = {
  course: CourseDetailEntity; route: CourseRouteEntity | null; timetable: TimetableRef | null;
  /** コースの所属ダイヤ。見出しのプルダウンで、どのダイヤのルートを出すかを選ぶ */
  timetables: TimetableRef[]; onSelectTimetable: (id: number) => void; disabled: boolean };
/** 編集中のルート。stops は全行（適用終了を含む）を今の位置のまま持つ */
type Draft = { loadingPlace: PlaceRef | null; loadingArrivalTime: string | null; loadingDepartureTime: string | null; stops: CourseStopEntity[] };
/** 時刻順の検査で止まった場所。'loading' は積地、数値は地点の id（追加行は負の仮 id） */
type TimeError = { at: 'loading' | number; message: string };

const draftOf = (route: CourseRouteEntity | null): Draft => ({
  loadingPlace: route?.loadingPlace ?? null,
  loadingArrivalTime: route?.loadingArrivalTime ?? null,
  loadingDepartureTime: route?.loadingDepartureTime ?? null,
  stops: route ? [...route.stops].sort((a, b) => a.sequenceNo - b.sequenceNo) : [],
});

const toTimeError = (found: ReturnType<typeof findTimeOrderError>, stops: CourseStopEntity[]): TimeError | null =>
  (found ? { at: found.stopIndex == null ? 'loading' : stops[found.stopIndex].id, message: found.message } : null);

/** 適用終了の行の位置を固定し、残りの枠に表示中の行を liveOrdered の順で入れる（判断事項 11） */
export const mergeLiveOrder = (all: CourseStopEntity[], liveOrdered: CourseStopEntity[]): CourseStopEntity[] => {
  const queue = [...liveOrdered];
  return all.map((s) => (s.endedOn ? s : (queue.shift() ?? s)));
};

/**
 * ルート（コース × ダイヤ。未所属なら timetable null）の積地と地点を項目ごとに即保存する（PATCH routes/:id。無ければ POST で未所属の並びを作る）。
 * 変更は draft に入れ、時刻順を検査してから送る。崩れていれば送らず該当行を赤くし、draft は残す（L2。判断事項 12）。
 * 送るのは毎回 積地 3 項目 ＋ 全行（移動時間・距離は読んだ値のまま）。送らなかった行を API が末尾に回さないため
 */
export default function RouteEditor({ course, route, timetable, timetables, onSelectTimetable, disabled }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const save = useSaveRouteMutation();
  const endStop = useEndStopMutation();
  const [draft, setDraft] = useState<Draft>(() => draftOf(route));
  const [error, setError] = useState<TimeError | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [endTarget, setEndTarget] = useState<CourseStopEntity | null>(null);
  const busy = disabled || save.isPending || endStop.isPending;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 同じルートのサーバの値が変わったら（保存の応答・再取得）、エラー中でなければ draft を作り直す。
  // ダイヤの切り替えは親が key で RouteEditor を作り直す（エラー中の draft を別ダイヤのルートへ送らない。判断事項 12）
  const errorRef = useRef(error);
  errorRef.current = error;
  // 保存失敗の catch はアンマウントや再描画の後に走るので、その時点の最新の route をここから読む
  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    if (!errorRef.current) setDraft(draftOf(route));
  }, [route]);

  const live = useMemo(() => draft.stops.filter((s) => !s.endedOn), [draft.stops]);
  const ended = useMemo(() => draft.stops.filter((s) => s.endedOn), [draft.stops]);
  // 「順」は適用終了・対象外を除いた地点だけで 1 から数える（固定配送順と同じ。log_web_issues#403 の 1）。対象外の行は番号を持たない
  const positions = useMemo(() => new Map(live.filter((s) => !s.excluded).map((s, i) => [s.id, i + 1])), [live]);

  const warnSaving = () => enqueueSnackbar('保存中です。終わってからもう一度変更してください', { variant: 'warning' });
  /**
   * 検査済みの draft を送る。呼ぶ前にコースのゲートを取っておく（同じコースの基本情報・適用終了と共通。判断事項 16）。
   * Why not mutate のコールバック: React Query は observer がアンマウント後だと mutate に渡したコールバックを呼ばないので、
   * 保存中にダイヤやコースを切り替えるとゲートが解放されずに残る。Promise の finally ならアンマウント後も必ず走る
   */
  const persist = (next: Draft) => {
    const data = {
      loading_place_id: next.loadingPlace?.id ?? null,
      loading_arrival_time: next.loadingArrivalTime,
      loading_departure_time: next.loadingDepartureTime,
      stops: next.stops.map((s) => toStopPayload(s)),
    };
    save.mutateAsync({ courseId: course.id, routeId: route?.id ?? null, data })
      .then(() => enqueueSnackbar('保存しました', { variant: 'success' }))
      .catch((e: unknown) => {
        enqueueSnackbar(parseCourseMasterError(e).message, { variant: 'error' });
        // 送った値を draft に残すと保存済みに見える。中身が同じ再取得では route の参照が変わらず作り直しの effect も走らないので、ここで戻す
        setDraft(draftOf(routeRef.current));
        setError(null);
      })
      .finally(() => releaseCourseSave(course.id));
  };
  // 重なりは draft を変える前に捨てる（捨てた変更が画面に残って「保存済み」に見えないように）
  const apply = (next: Draft) => {
    if (!tryAcquireCourseSave(course.id)) {
      warnSaving();
      return;
    }
    const found = toTimeError(findTimeOrderError(next), next.stops);
    setDraft(next);
    setError(found);
    if (found) {
      releaseCourseSave(course.id);
      enqueueSnackbar(found.message, { variant: 'error' });
      return;
    }
    persist(next);
  };
  /** 未保存の追加行（負の仮 id）を draft から外す。残りで検査し直し、通れば保存する（他の編集が残っていれば一緒に送る） */
  const cancelAdded = (id: number) => apply({ ...draft, stops: draft.stops.filter((s) => s.id !== id) });
  const patchStop = (id: number, patch: Partial<CourseStopEntity>) => apply({
    ...draft,
    stops: draft.stops.map((s) => (s.id === id ? { ...s, ...patch } : s)),
  });
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (busy || !over || active.id === over.id) return;
    const from = live.findIndex((s) => s.id === active.id);
    const to = live.findIndex((s) => s.id === over.id);
    if (from >= 0 && to >= 0) apply({ ...draft, stops: mergeLiveOrder(draft.stops, arrayMove(live, from, to)) });
  };
  const addStop = (place: PlaceRef, arrivalTime: string | null) => apply({
    ...draft,
    stops: [...draft.stops, {
      id: -Date.now(),
      sequenceNo: draft.stops.length + 1,
      place,
      arrivalTime,
      departureTime: null,
      drivingDurationSeconds: null,
      drivingDistanceMm: null,
      excluded: false,
      endedOn: null,
    }],
  });
  // 適用終了は draft を経由しない API（開いている並びだけ。log_web_issues#403 の 5）なので、応答の endedOn を draft に写してから時刻順を見直す（エラー中の編集は残す。判断事項 12）。
  // 見直しで通るようになり、エラー中に溜めた編集（時刻・並び）が残っていれば続けてルートも保存する（即保存の約束を守る）
  // 解除（endedOn: null）は画面から使わない。戻したいときは地点を追加する（log_web_issues#384。API の解除は残す）
  const end = (stop: CourseStopEntity) => {
    // 保存済みの行（適用終了を出す行）はルートにしか無い。route が無いのは未所属の並びをまだ作っていないときだけ
    if (route == null) return;
    if (!tryAcquireCourseSave(course.id)) {
      warnSaving();
      return;
    }
    const hadError = error != null;
    // Why not finally で無条件に解放: ルートの保存へ引き継いだゲートを、その保存の途中で空けてしまう（解放は persist の finally に任せる）
    let handedOver = false;
    // アンマウント後もゲートを解放するため mutateAsync の Promise で続ける（persist の Why not と同じ）
    endStop.mutateAsync({ courseId: course.id, routeId: route.id, placeId: stop.place.id, endedOn: todayString() })
      .then((detail) => {
        const saved = detail.routes.find((r) => r.id === route.id);
        const next = {
          ...draft,
          stops: draft.stops.map((s) => {
            const srv = saved?.stops.find((x) => x.id === s.id);
            return srv ? { ...s, endedOn: srv.endedOn } : s;
          }),
        };
        const found = toTimeError(findTimeOrderError(next), next.stops);
        setDraft(next);
        setError(found);
        enqueueSnackbar('適用終了にしました', { variant: 'success' });
        if (!found && hadError) {
          handedOver = true;
          persist(next);
        }
      })
      .catch((e: unknown) => enqueueSnackbar(parseCourseMasterError(e).message, { variant: 'error' }))
      .finally(() => {
        if (!handedOver) releaseCourseSave(course.id);
      });
  };
  return (
    <Box>
      {/* 見出しはサンプル（route-planner 1fbdc70 の CourseOrderScope）の形: 左に「配送順」＋ダイヤの選択欄、同じ行の右に「配送先を追加」（log_web_issues#403 の 7）。
          選択欄は 2 つ以上のダイヤに入っているときだけ。1 つ・未所属は上の「所属ダイヤ」で分かるので「配送順」の文字だけにする。
          読み上げの名前は SelectDisplayProps に付ける（combobox になるのは表示側の要素で、隠れた input ではない）。
          Why not サンプルの「{コース番号}の基本ルール」「基本の順に戻す」: 並びはダイヤごとに持ち、共通の並びは持たない（PM 提案 2026-10-27 14:35） */}
      <Box data-testid="route-heading" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>配送順</Typography>
        {timetable && timetables.length > 1 && (
          <TextField
            select
            size="small"
            value={timetable.id}
            onChange={(e) => onSelectTimetable(Number(e.target.value))}
            slotProps={{ select: { SelectDisplayProps: { 'aria-label': 'ダイヤ' } } }}
            sx={{ minWidth: 220, '& .MuiSelect-select': { py: 0.75, fontSize: 14, fontWeight: 600 } }}
          >
            {timetables.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setAddOpen(true)} disabled={busy}>配送先を追加</Button>
      </Box>
      {/* DndContext も role=status の読み上げ要素を作るので、テストは data-testid で見る */}
      {error && (
        <Typography id="route-time-error" role="status" data-testid="route-time-error" sx={{ fontSize: 13, color: 'error.main', mb: 1 }}>
          {error.message}
        </Typography>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: STOP_COLS, minWidth: 900, gap: 1, fontSize: 13, color: muted, px: 1, pb: 1 }}>
          <span />
          <span>順</span>
          <span>地点コード</span>
          <span>地点名 / 住所</span>
          <span>到着予定</span>
          <span>出発</span>
          <span>滞在</span>
          <span />
        </Box>
        <LoadingPlaceRow
          place={draft.loadingPlace}
          arrivalTime={draft.loadingArrivalTime}
          departureTime={draft.loadingDepartureTime}
          invalid={error?.at === 'loading'}
          disabled={busy}
          onPlace={(place) => apply({ ...draft, loadingPlace: place })}
          onTime={(field, value) => apply({ ...draft, [field]: value })}
        />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={live.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {live.map((s) => (
              <StopRow
                key={s.id}
                stop={s}
                position={positions.get(s.id) ?? null}
                invalid={error?.at === s.id}
                unsaved={s.id <= 0}
                disabled={busy}
                onTime={(field, value) => patchStop(s.id, { [field]: value })}
                onEnd={() => (s.id <= 0 ? cancelAdded(s.id) : setEndTarget(s))}
              />
            ))}
          </SortableContext>
        </DndContext>
        {live.length === 0 && <Typography sx={{ color: muted, py: 2 }}>配送先なし</Typography>}
      </Box>
      {ended.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ fontSize: 13, color: muted, mb: 0.5 }}>
            適用終了した配送地点（履歴として保持）
          </Box>
          {ended.map((s) => (
            <Box key={s.id} sx={{ fontSize: 13, color: muted, textDecoration: 'line-through' }}>{s.place.name}（{s.endedOn} 終了）</Box>
          ))}
        </Box>
      )}
      <AddStopDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={(place, arrivalTime) => {
          setAddOpen(false);
          addStop(place, arrivalTime);
        }}
      />
      <EndStopDialog
        stop={endTarget}
        courseCode={course.code}
        timetableName={route?.timetable?.name ?? null}
        onClose={() => setEndTarget(null)}
        onConfirm={() => {
          if (endTarget) end(endTarget);
          setEndTarget(null);
        }}
      />
    </Box>
  );
}
